// Notification and popup system
// Based on documentation from docs/01_HOMEPAGE_AND_NAVIGATION.md

/**
 * Show custom popup notification
 * @param {object} options Popup options
 * @param {string} options.title Popup title
 * @param {string} options.description Popup description
 * @param {string} options.type Popup type (success, error, warning, info)
 * @param {number} options.duration Duration in seconds (auto-calculated if not provided)
 */
export function showPopup({ title, description, type = 'info', duration = null }) {
  // Remove existing popup
  const existingPopup = document.querySelector('.custom-popup');
  if (existingPopup) {
    existingPopup.remove();
  }

  // Icon definitions
  const icons = {
    success: '✓',
    error: '✗',
    warning: '⚠',
    info: 'ℹ'
  };

  // Create popup element
  const popup = document.createElement('div');
  popup.className = `custom-popup popup-${type}`;
  
  // Build HTML
  popup.innerHTML = `
    <div class="popup-icon">${icons[type] || icons.info}</div>
    <div class="popup-content">
      <h3>${title}</h3>
      <p>${description}</p>
    </div>
    <button class="popup-close">&times;</button>
  `;
  
  // Add to page
  document.body.appendChild(popup);
  
  // Close button handler
  const closeBtn = popup.querySelector('.popup-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      popup.remove();
    });
  }
  
  // Auto-close after duration based on text length
  const autoDuration = duration || Math.max(5, Math.round(description.split(' ').length / 5));
  setTimeout(() => {
    if (popup.parentElement) {
      popup.remove();
    }
  }, autoDuration * 1000);
}

/**
 * Show toast notification (smaller, bottom-right)
 * @param {string} message Toast message
 * @param {string} type Toast type (success, error, warning, info)
 * @param {number} duration Duration in milliseconds
 */
export function showToast(message, type = 'info', duration = 3000) {
  const toast = document.createElement('div');
  toast.className = `toast-notification toast-${type}`;
  toast.textContent = message;
  
  document.body.appendChild(toast);
  
  // Trigger animation
  setTimeout(() => {
    toast.classList.add('show');
  }, 10);
  
  // Remove after duration
  setTimeout(() => {
    toast.classList.remove('show');
    setTimeout(() => {
      if (toast.parentElement) {
        toast.remove();
      }
    }, 300);
  }, duration);
}

/**
 * Confirm dialog
 * @param {string} message Confirmation message
 * @param {Function} onConfirm Callback on confirm
 * @param {Function} onCancel Callback on cancel
 */
export function showConfirm(message, onConfirm, onCancel = () => {}) {
  const overlay = document.createElement('div');
  overlay.className = 'confirm-overlay';
  
  const dialog = document.createElement('div');
  dialog.className = 'confirm-dialog';
  dialog.innerHTML = `
    <p>${message}</p>
    <div class="confirm-buttons">
      <button class="btn btn-secondary confirm-cancel">Cancel</button>
      <button class="btn btn-primary confirm-ok">OK</button>
    </div>
  `;
  
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);
  
  const handleConfirm = () => {
    overlay.remove();
    onConfirm();
  };
  
  const handleCancel = () => {
    overlay.remove();
    onCancel();
  };
  
  dialog.querySelector('.confirm-ok').addEventListener('click', handleConfirm);
  dialog.querySelector('.confirm-cancel').addEventListener('click', handleCancel);
  
  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) {
      handleCancel();
    }
  });
}

/**
 * Show loading spinner
 * @param {string} message Loading message
 * @returns {Function} Function to hide the loader
 */
export function showLoader(message = 'Loading...') {
  const loader = document.createElement('div');
  loader.className = 'loader-overlay';
  loader.innerHTML = `
    <div class="loader-content">
      <div class="spinner"></div>
      <p>${message}</p>
    </div>
  `;
  
  document.body.appendChild(loader);
  
  return () => {
    if (loader.parentElement) {
      loader.remove();
    }
  };
}
